import { Fragment, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';
import { ActionButton } from '../../../../components/buttons/ActionButton';
import {
  formatVeralliaDashboardDateTime,
  formatVeralliaDashboardSize,
  getVeralliaDashboardDefectLabel,
  getVeralliaDashboardShift,
  sortVeralliaDashboardDetections,
} from '../../../../services/dashboard/verallia-dashboard-service';
import {
  VeralliaDashboardDetectionImage,
  VeralliaDashboardNoData,
} from './VeralliaDashboardBlocks';

const PAGE_SIZE = 25;

export const VeralliaDashboardDetectionsTable = ({
                                                   detections,
                                                   page,
                                                   onPageChange,
                                                 }) => {
  const [expandedIds, setExpandedIds] = useState([]);

  const sortedDetections = sortVeralliaDashboardDetections(detections);
  const totalPages = Math.max(1, Math.ceil(sortedDetections.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pageDetections = sortedDetections.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const toggleDetection = (detectionId) => {
    setExpandedIds((currentIds) => {
      return currentIds.includes(detectionId)
        ? currentIds.filter((id) => id !== detectionId)
        : [...currentIds, detectionId];
    });
  };

  return (
    <div className="overflow-hidden rounded-md border border-perception-black-500">
      <div className="max-h-[620px] overflow-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-[1] bg-perception-black-700">
          <tr className="border-b border-perception-black-500">
            <th className="w-10 px-3 py-3"/>

            {[
              'Date',
              'Line',
              'Pallet ID',
              'Defect',
              'Size',
              'Shift',
            ].map((column) => (
              <th
                key={column}
                className="px-4 py-3 text-left text-xs font-semibold uppercase text-perception-gray-500"
              >
                {column}
              </th>
            ))}
          </tr>
          </thead>

          <tbody>
          {pageDetections.map((detection) => {
            const isExpanded = expandedIds.includes(detection.id);

            return (
              <Fragment key={detection.id}>
                <tr
                  key={detection.id}
                  className="cursor-pointer border-b border-perception-black-500 bg-perception-black-900 hover:bg-perception-black-700"
                  onClick={() => toggleDetection(detection.id)}
                >
                  <td className="px-3 py-3 text-perception-gray-600">
                    {isExpanded ? (
                      <ChevronDownIcon className="size-4"/>
                    ) : (
                      <ChevronRightIcon className="size-4"/>
                    )}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm text-perception-gray-300">
                    {formatVeralliaDashboardDateTime(detection.timestamp)}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    {detection.line}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    {detection.palletId}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    {getVeralliaDashboardDefectLabel(detection.defect)}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    {formatVeralliaDashboardSize(detection.size)}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    {getVeralliaDashboardShift(detection.timestamp)}
                  </td>
                </tr>

                {isExpanded && (
                  <tr
                    key={`${detection.id}-details`}
                    className="border-b border-perception-black-500 bg-perception-black-800"
                  >
                    <td colSpan={7} className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="text-xs font-semibold uppercase text-perception-gray-600">
                          Camera image
                        </div>

                        <VeralliaDashboardDetectionImage
                          src={detection.image}
                          alt={detection.palletId}
                          className="aspect-video w-72"
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}

          {pageDetections.length === 0 && (
            <tr>
              <td colSpan={7}>
                <VeralliaDashboardNoData />
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>

      {sortedDetections.length > 0 && (
        <div className="flex items-center justify-end gap-3 border-t border-perception-black-500 bg-perception-black-700 px-4 py-3">
          <ActionButton
            text="Previous"
            color="secondary"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
          />

          <div className="text-xs uppercase text-perception-gray-600">
            Page {safePage} / {totalPages}
          </div>

          <ActionButton
            text="Next"
            color="secondary"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(safePage + 1)}
          />
        </div>
      )}
    </div>
  );
};